import React, { useState, useEffect, useCallback } from 'react';
import './Logbook.css';  // reuse the exact Logbook styling
import './IndyPicks.css'; // small overrides: add-form row + delete button
import './App.css';       // company-popup modal styles

const IndyPicks = () => {
  const [picks, setPicks] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [quotesRefreshing, setQuotesRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addSymbol, setAddSymbol] = useState('');
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'error'|'success', message }
  const [sundayOnly, setSundayOnly] = useState(false);
  const [companyPopup, setCompanyPopup] = useState({ show: false, data: null, loading: false });

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchPicks = useCallback(async (opts = {}) => {
    try {
      if (!opts.silent) setLoading(true);
      const res = await fetch(`${API_URL}/api/indypicks`);
      if (!res.ok) throw new Error('Failed to load IndyPicks');
      const data = await res.json();
      setPicks(data.picks || []);
      setQuotes(data.quotes || {});
      setQuotesRefreshing(!!data.refresh_in_progress);
      setError(null);
      if (data.refresh_in_progress) {
        setTimeout(() => fetchPicks({ silent: true }), 4000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchPicks();
  }, [fetchPicks]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const sym = addSymbol.trim().toUpperCase();
    if (!sym) return;
    setAdding(true);
    try {
      const res = await fetch(`${API_URL}/api/indypicks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol: sym }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast('error', data.detail || `Failed to add ${sym}`);
      } else {
        showToast('success', `${sym} added — computing stops…`);
        setAddSymbol('');
        fetchPicks({ silent: true });
      }
    } catch (err) {
      showToast('error', err.message || 'Network error');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (symbol) => {
    if (!window.confirm(`Remove ${symbol} from IndyPicks?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/indypicks/${symbol}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast('error', data.detail || `Failed to delete ${symbol}`);
      } else {
        showToast('success', `${symbol} removed`);
        setPicks((prev) => prev.filter((p) => p.symbol !== symbol));
      }
    } catch (err) {
      showToast('error', err.message || 'Network error');
    }
  };

  const handleRefresh = async () => {
    try {
      await fetch(`${API_URL}/api/indypicks/quotes/refresh`, { method: 'POST' });
      setQuotesRefreshing(true);
      setTimeout(() => fetchPicks({ silent: true }), 2000);
    } catch (err) {
      showToast('error', 'Refresh failed');
    }
  };

  // Company popup handlers (identical to Logbook)
  const handleCompanyClick = async (pick, e) => {
    e.preventDefault();
    setCompanyPopup({ show: true, data: { symbol: pick.symbol, company_name: pick.company_name }, loading: true });
    try {
      const res = await fetch(`${API_URL}/api/company/${pick.symbol}?_=${Date.now()}`, { cache: 'no-store' });
      const data = await res.json();
      setCompanyPopup({
        show: true,
        data: {
          symbol: pick.symbol,
          company_name: pick.company_name,
          business_description: data.business_description || '',
          company_description_full: data.company_description_full || data.message || 'Company description not available.',
        },
        loading: false,
      });
    } catch (err) {
      setCompanyPopup({
        show: true,
        data: { symbol: pick.symbol, company_name: pick.company_name, business_description: '', company_description_full: 'Could not load description.' },
        loading: false,
      });
    }
  };

  const closeCompanyPopup = () => setCompanyPopup({ show: false, data: null, loading: false });

  useEffect(() => {
    const onClickOutside = (event) => {
      if (companyPopup.show && !event.target.closest('.company-popup') && !event.target.closest('.company-name-clickable')) {
        closeCompanyPopup();
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [companyPopup.show]);

  if (loading) {
    return (
      <div className="logbook-container">
        <div className="logbook-loading">
          <div className="spinner"></div>
          <p>Loading IndyPicks…</p>
        </div>
      </div>
    );
  }

  const filtered = picks.filter(
    (p) => !sundayOnly || quotes[p.symbol]?.sunday_changed || quotes[p.symbol]?.sunday_stopped,
  );
  const sundayCount = picks.filter(
    (p) => quotes[p.symbol]?.sunday_changed || quotes[p.symbol]?.sunday_stopped,
  ).length;

  return (
    <div className="logbook-container">
      <header className="logbook-header">
        <h1>🎯 IndyPicks</h1>
        <p className="logbook-subtitle">Hand-picked positions — Entry Stop + Sunday Stop, tracked until sold</p>
      </header>

      <div className="logbook-stats">
        <div className="stat-card">
          <span className="stat-value">{picks.length}</span>
          <span className="stat-label">Positions Tracked</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{sundayCount}</span>
          <span className="stat-label">Sunday Stop Movers</span>
        </div>
      </div>

      {/* Add-pick form */}
      <form className="indypicks-add-form" onSubmit={handleAdd} data-testid="indypicks-add-form">
        <input
          type="text"
          className="indypicks-add-input"
          placeholder="Ticker (e.g. NVDA)"
          value={addSymbol}
          onChange={(e) => setAddSymbol(e.target.value.toUpperCase())}
          maxLength={10}
          disabled={adding}
          data-testid="indypicks-symbol-input"
        />
        <button
          type="submit"
          className="indypicks-add-btn"
          disabled={adding || !addSymbol.trim()}
          data-testid="indypicks-add-btn"
        >
          {adding ? 'Adding…' : '+ Add Pick'}
        </button>
      </form>

      <div className="logbook-actions">
        <button className="refresh-btn" onClick={handleRefresh} data-testid="indypicks-refresh-btn">
          🔄 Refresh Prices
        </button>
        <button
          className={`sunday-review-btn ${sundayOnly ? 'active' : ''}`}
          onClick={() => setSundayOnly(!sundayOnly)}
          title="Show only picks whose Sunday Stop moved up or triggered this week"
          data-testid="indypicks-sunday-toggle"
        >
          🗓️ Sunday Review ({sundayCount})
        </button>
        {quotesRefreshing && (
          <span className="quotes-refreshing-tag" data-testid="indypicks-refreshing-tag">
            Refreshing prices…
          </span>
        )}
      </div>

      {error && (
        <div className="logbook-error" style={{ margin: '1rem 0' }}>
          <p>{error}</p>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="logbook-empty">
          <p>{picks.length === 0
            ? 'No picks yet. Enter a ticker above to start tracking.'
            : 'No picks match the Sunday Review filter.'}</p>
        </div>
      ) : (
        <div className="logbook-table-wrapper">
          <table className="logbook-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Company</th>
                <th>Current Price</th>
                <th title="Stop loss = max gap between price - (2 × ATR) and price - 7%, whichever is looser">
                  Entry Stop
                </th>
                <th title="Sunday Stop = weekly ratchet: max(previous stop, confirmed weekly pivot low − 0.5% buffer, chandelier trail). Only moves UP. ⬆ = raised this week. 🎯 = +20% from anchor, chandelier tightened to 1.5×ATR. 🔴 = Friday close below stop.">
                  Sunday Stop
                </th>
                <th>Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((pick) => {
                const q = quotes[pick.symbol];
                return (
                  <tr key={pick.symbol}>
                    <td className="symbol-cell">
                      <a
                        href={`https://finance.yahoo.com/quote/${pick.symbol}/chart/`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {pick.symbol}
                      </a>
                    </td>
                    <td className="company-cell">
                      <div
                        className="company-name company-name-clickable"
                        onClick={(e) => handleCompanyClick(pick, e)}
                        title="Click for company description"
                      >
                        {pick.company_name}
                      </div>
                    </td>
                    <td className="price-cell" data-testid={`indypicks-price-${pick.symbol}`}>
                      {q?.price !== undefined
                        ? `$${q.price.toFixed(2)}`
                        : <span className="quote-pending">—</span>}
                    </td>
                    <td className="stop-cell" data-testid={`indypicks-stop-${pick.symbol}`}>
                      {q?.stop_loss !== undefined
                        ? (
                          <span
                            title={`Rule: ${q.stop_rule}  •  2×ATR stop: $${q.stop_atr?.toFixed(2)}  •  7% stop: $${q.stop_pct?.toFixed(2)}`}
                            className="stop-value"
                          >
                            ${q.stop_loss.toFixed(2)}
                          </span>
                        )
                        : <span className="quote-pending">—</span>}
                    </td>
                    <td className="trail-cell" data-testid={`indypicks-sunday-${pick.symbol}`}>
                      {(() => {
                        if (q?.sunday_stop == null) return <span className="quote-pending">—</span>;
                        const tip = [
                          `Rule: ${q.sunday_reason}`,
                          q.sunday_prev_stop != null ? `Previous: $${q.sunday_prev_stop.toFixed(2)}` : null,
                          q.sunday_pivot != null ? `Pivot support: $${q.sunday_pivot.toFixed(2)}` : null,
                          q.sunday_chandelier != null ? `Chandelier: $${q.sunday_chandelier.toFixed(2)}` : null,
                          q.sunday_gain_pct != null ? `Gain from anchor (${q.sunday_anchor_date}): ${q.sunday_gain_pct}%` : null,
                          `Week of ${q.sunday_week_of || '—'}`,
                        ].filter(Boolean).join('  •  ');
                        return (
                          <span
                            title={tip}
                            className={`trail-value ${q.sunday_stopped ? 'sunday-stopped' : q.sunday_changed ? 'sunday-raised' : ''}`}
                          >
                            {q.sunday_stopped && '🔴 '}
                            ${q.sunday_stop.toFixed(2)}
                            {!q.sunday_stopped && q.sunday_changed && ' ⬆'}
                            {!q.sunday_stopped && q.sunday_tighten && ' 🎯'}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="date-cell">
                      {pick.added_at ? new Date(pick.added_at).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      <button
                        className="indypicks-delete-btn"
                        onClick={() => handleDelete(pick.symbol)}
                        title={`Sold ${pick.symbol}? Remove from IndyPicks`}
                        data-testid={`indypicks-delete-${pick.symbol}`}
                      >
                        🗑️ Sold
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <footer className="logbook-footer">
        <p>Pro Stock Money BOWL — IndyPicks (independent positions)</p>
      </footer>

      {/* Toast */}
      {toast && (
        <div className={`indypicks-toast indypicks-toast-${toast.type}`} data-testid="indypicks-toast">
          {toast.message}
        </div>
      )}

      {/* Company Description Popup */}
      {companyPopup.show && companyPopup.data && (
        <div className="company-popup">
          <div className="company-popup-content">
            <div className="company-popup-header">
              <span className="company-popup-symbol">{companyPopup.data.symbol}</span>
              <span className="company-popup-title">{companyPopup.data.company_name}</span>
              <button className="company-popup-close" onClick={closeCompanyPopup}>×</button>
            </div>
            <div className="company-popup-details">
              {companyPopup.data.business_description && (
                <div className="company-popup-industry">
                  <span className="industry-label">{companyPopup.data.business_description}</span>
                </div>
              )}
              <div className="company-popup-description">
                {companyPopup.loading ? (
                  <p><em>Loading description…</em></p>
                ) : (
                  <p>{companyPopup.data.company_description_full || 'Company description not available.'}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndyPicks;
